import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Users,
  DollarSign,
  TrendingUp,
  UserPlus,
  Percent,
  Facebook,
  Store,
  Calendar,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Copy,
  Check,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Wallet,
  Gift,
  Ticket,
  ArrowRightLeft,
  ExternalLink,
  Sparkles,
} from "lucide-react";

const PartnerDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [partner, setPartner] = useState(null);
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedPromoCode, setCopiedPromoCode] = useState(false);
  const [personalPromoCode, setPersonalPromoCode] = useState(null);
  const [referralTransfers, setReferralTransfers] = useState([]);
  
  // Pagination states
  const [referralsPage, setReferralsPage] = useState(1);
  const [commissionsPage, setCommissionsPage] = useState(1);
  const [referralsTotalPages, setReferralsTotalPages] = useState(1);
  const [commissionsTotalPages, setCommissionsTotalPages] = useState(1);
  
  // Filters
  const [commissionsFilter, setCommissionsFilter] = useState("all");
  const [referralsFilter, setReferralsFilter] = useState("all");

  // Fetch partner dashboard
  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/partners/my-dashboard`);
      setPartner(response.data.partner);
      setStats(response.data.stats);
      setReferralTransfers(response.data.referral_transfers || []);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      if (error.response?.status === 404) {
        toast.error("Vous n'êtes pas un partenaire enregistré");
      } else {
        toast.error("Erreur lors du chargement du tableau de bord");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch personal promo code (if any given by admin)
  const fetchPersonalPromoCode = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/my-promo-code`);
      if (response.data.has_promo_code) {
        setPersonalPromoCode(response.data);
      }
    } catch (error) {
      console.error("Error fetching personal promo code:", error);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchPersonalPromoCode();
  }, [fetchDashboard, fetchPersonalPromoCode]);

  // Copy promo code
  const copyPromoCode = () => {
    if (partner?.promo_code) {
      navigator.clipboard.writeText(partner.promo_code);
      setCopiedCode(true);
      toast.success("Code promo copié!");
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  // Copy personal promo code
  const copyPersonalPromoCode = () => {
    if (personalPromoCode?.promo_code) {
      navigator.clipboard.writeText(personalPromoCode.promo_code);
      setCopiedPromoCode(true);
      toast.success("Code promo personnel copié!");
      setTimeout(() => setCopiedPromoCode(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="text-center py-12">
        <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Compte partenaire non trouvé
        </h3>
        <p className="text-gray-500">
          Vous n'êtes pas encore enregistré comme partenaire Middleman.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="partner-dashboard">
      {/* Personal Promo Code Banner (if received from admin) */}
      {personalPromoCode && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-4 text-white">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-full">
                <Gift className="h-5 w-5" />
              </div>
              <div>
                <p className="font-semibold">🎁 Vous avez un code promo personnel!</p>
                <p className="text-white/90 text-sm">
                  {personalPromoCode.message || `${personalPromoCode.discount_rate}% de réduction sur vos transferts`}
                </p>
                {personalPromoCode.code_type === 'multi_uses_limit' && personalPromoCode.uses_remaining && (
                  <p className="text-white/80 text-xs">{personalPromoCode.uses_remaining} utilisation(s) restante(s)</p>
                )}
                {personalPromoCode.expires_at && (
                  <p className="text-white/80 text-xs">Expire le {new Date(personalPromoCode.expires_at).toLocaleDateString('fr-CA')}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur rounded-lg px-4 py-2">
              <code className="font-mono font-bold text-lg">{personalPromoCode.promo_code}</code>
              <button
                onClick={copyPersonalPromoCode}
                className="p-1.5 hover:bg-white/20 rounded transition-colors"
              >
                {copiedPromoCode ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header with Partner Info */}
      <div className="bg-gradient-to-r from-brand-600 to-brand-700 rounded-xl p-6 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              {partner.partner_type === "admin_groupe" ? (
                <Facebook className="h-5 w-5" />
              ) : (
                <Store className="h-5 w-5" />
              )}
              <Badge className="bg-white/20 text-white border-white/30">
                {partner.partner_type === "admin_groupe" ? "Admin Groupe" : "Revendeur"}
              </Badge>
            </div>
            <h1 className="text-2xl font-bold">
              {partner.facebook_group_name || partner.company_name || "Mon Tableau de Bord"}
            </h1>
            <p className="text-brand-100 text-sm mt-1">
              Bienvenue dans votre espace partenaire Middleman
            </p>
          </div>
          
          {/* Promo Code Card */}
          <div className="bg-white/10 backdrop-blur rounded-lg p-4 min-w-[200px]">
            <p className="text-xs text-brand-100 mb-1">Votre code promo</p>
            <div className="flex items-center gap-2">
              <code className="text-xl font-bold font-mono">
                {partner.promo_code}
              </code>
              <button
                onClick={copyPromoCode}
                className="p-1.5 hover:bg-white/20 rounded transition-colors"
              >
                {copiedCode ? (
                  <Check className="h-4 w-4 text-green-300" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Facebook Group Promotion Banner */}
      {partner.partner_type === "admin_groupe" && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <Facebook className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-gray-900">Augmentez votre visibilité!</h3>
                  <Sparkles className="h-4 w-4 text-amber-500" />
                </div>
                <p className="text-gray-600 text-sm max-w-lg">
                  Ajoutez votre groupe <strong>{partner.facebook_group_name}</strong> à notre répertoire de groupes Facebook. 
                  Les acheteurs et vendeurs de billets pourront vous découvrir et rejoindre votre communauté!
                </p>
                <ul className="mt-2 text-xs text-gray-500 space-y-1">
                  <li className="flex items-center gap-1">
                    <Check className="h-3 w-3 text-green-500" />
                    Plus de membres potentiels pour votre groupe
                  </li>
                  <li className="flex items-center gap-1">
                    <Check className="h-3 w-3 text-green-500" />
                    Plus de filleuls utilisant votre code promo
                  </li>
                  <li className="flex items-center gap-1">
                    <Check className="h-3 w-3 text-green-500" />
                    Avantage exclusif d'être partenaire Middleman
                  </li>
                </ul>
              </div>
            </div>
            <div className="flex flex-col gap-2 w-full md:w-auto">
              <Button 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => window.open('/facebook-groups', '_blank')}
              >
                <Facebook className="h-4 w-4 mr-2" />
                Voir les groupes
                <ExternalLink className="h-3 w-3 ml-2" />
              </Button>
              <Button 
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                onClick={() => window.location.href = '/contact?subject=Ajouter mon groupe Facebook'}
              >
                Demander l'ajout
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <UserPlus className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Filleuls</p>
                <p className="text-2xl font-bold">
                  {stats?.total_referrals || 0}
                </p>
                <p className="text-xs text-green-600">
                  {stats?.active_referrals || 0} actifs
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Ce mois</p>
                <p className="text-2xl font-bold">
                  ${(stats?.commissions_this_month || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Cette année</p>
                <p className="text-2xl font-bold">
                  ${(stats?.commissions_this_year || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Wallet className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">En attente</p>
                <p className="text-2xl font-bold">
                  ${(stats?.commissions_pending || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commission Rates */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Percent className="h-4 w-4 text-brand-600" />
            Vos taux de commission
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-4 bg-brand-50 rounded-lg">
              <p className="text-2xl font-bold text-brand-600">
                {partner.commission_rate_first}%
              </p>
              <p className="text-xs text-gray-600">
                Commission sur le 1er transfert
              </p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-600">
                {partner.commission_rate_recurring}%
              </p>
              <p className="text-xs text-gray-600">
                Commission récurrente
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">
                {partner.discount_rate_first}%
              </p>
              <p className="text-xs text-gray-600">
                Réduction pour vos membres
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Referrals, Transfers and Commissions */}
      <Tabs defaultValue="referrals" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="referrals">
            <UserPlus className="h-4 w-4 mr-2" />
            Filleuls
          </TabsTrigger>
          <TabsTrigger value="transfers">
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            Transferts
          </TabsTrigger>
          <TabsTrigger value="commissions">
            <DollarSign className="h-4 w-4 mr-2" />
            Commissions
          </TabsTrigger>
        </TabsList>

        {/* Referrals Tab */}
        <TabsContent value="referrals">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-base">Filleuls inscrits</CardTitle>
                  <CardDescription>
                    Utilisateurs inscrits avec votre code promo
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchDashboard}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {stats?.recent_referrals?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Utilisateur</TableHead>
                      <TableHead>Date d'inscription</TableHead>
                      <TableHead>Statut 1er transfert</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.recent_referrals.map((ref) => (
                      <TableRow key={ref.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{ref.user_name || "—"}</p>
                            <p className="text-xs text-gray-500">{ref.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-gray-500">
                            <Calendar className="h-3 w-3" />
                            {new Date(ref.created_at).toLocaleDateString("fr-CA")}
                          </div>
                        </TableCell>
                        <TableCell>
                          {ref.first_transfer_completed ? (
                            <Badge className="bg-green-100 text-green-800 border-green-200">
                              <Check className="h-3 w-3 mr-1" />
                              Complété
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                              <Clock className="h-3 w-3 mr-1" />
                              En attente
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <UserPlus className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun filleul pour le moment</p>
                  <p className="text-sm mt-2">
                    Partagez votre code <code className="bg-gray-100 px-2 py-1 rounded font-mono">{partner.promo_code}</code> pour commencer!
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transfers Tab - NEW */}
        <TabsContent value="transfers">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-base">Transferts de mes filleuls</CardTitle>
                  <CardDescription>
                    Tous les transferts effectués par vos filleuls
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchDashboard}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {referralTransfers?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Filleul</TableHead>
                      <TableHead>Événement</TableHead>
                      <TableHead className="text-right">Montant</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Votre commission</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referralTransfers.map((transfer) => (
                      <TableRow key={transfer.id}>
                        <TableCell className="text-sm text-gray-500">
                          {new Date(transfer.created_at).toLocaleDateString("fr-CA")}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{transfer.referral_name || "—"}</p>
                            <p className="text-xs text-gray-500">{transfer.referral_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[200px]">
                            <p className="font-medium text-sm truncate">{transfer.event_name || "—"}</p>
                            <p className="text-xs text-gray-500">{transfer.tickets_count || 0} billet(s)</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          ${transfer.total_amount?.toFixed(2) || "0.00"}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            transfer.status === "completed" ? "bg-green-100 text-green-800" :
                            transfer.status === "in_progress" ? "bg-blue-100 text-blue-800" :
                            transfer.status === "pending" ? "bg-yellow-100 text-yellow-800" :
                            "bg-gray-100 text-gray-800"
                          }>
                            {transfer.status === "completed" ? "Complété" :
                             transfer.status === "in_progress" ? "En cours" :
                             transfer.status === "pending" ? "En attente" :
                             transfer.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {transfer.commission_amount ? (
                            <span className="font-medium text-green-600">
                              +${transfer.commission_amount.toFixed(2)}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <ArrowRightLeft className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun transfert effectué par vos filleuls</p>
                  <p className="text-sm mt-2">
                    Les transferts de vos filleuls apparaîtront ici.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Commissions Tab */}
        <TabsContent value="commissions">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-base">Historique des commissions</CardTitle>
                  <CardDescription>
                    Commissions gagnées sur les transferts de vos filleuls
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchDashboard}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {stats?.recent_commissions?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Montant transfert</TableHead>
                      <TableHead>Commission</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.recent_commissions.map((com) => (
                      <TableRow key={com.id}>
                        <TableCell>
                          {new Date(com.created_at).toLocaleDateString("fr-CA")}
                        </TableCell>
                        <TableCell>
                          ${com.transaction_amount?.toFixed(2)}
                        </TableCell>
                        <TableCell className="font-medium text-green-600">
                          +${com.commission_amount?.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {com.is_first_transfer ? "1er transfert" : "Récurrent"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {com.status === "paid" ? (
                            <Badge className="bg-green-100 text-green-800">
                              Payé
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800">
                              En attente
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune commission pour le moment</p>
                  <p className="text-sm mt-2">
                    Vous gagnerez des commissions lorsque vos filleuls effectueront des transferts.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* How it works info */}
      <Card className="bg-gradient-to-r from-brand-50 to-purple-50 border-brand-200">
        <CardHeader>
          <CardTitle className="text-base">Comment fonctionnent vos commissions?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-brand-100 rounded-full">
                <span className="font-bold text-brand-600">1</span>
              </div>
              <div>
                <p className="font-medium">Partagez votre code</p>
                <p className="text-gray-600">
                  Vos membres utilisent votre code <code className="bg-white px-1 rounded">{partner.promo_code}</code> lors de leur inscription
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-brand-100 rounded-full">
                <span className="font-bold text-brand-600">2</span>
              </div>
              <div>
                <p className="font-medium">Réduction automatique</p>
                <p className="text-gray-600">
                  Ils bénéficient de {partner.discount_rate_first}% de réduction sur leur premier transfert
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-brand-100 rounded-full">
                <span className="font-bold text-brand-600">3</span>
              </div>
              <div>
                <p className="font-medium">Vous gagnez</p>
                <p className="text-gray-600">
                  {partner.commission_rate_first}% sur le 1er transfert, puis {partner.commission_rate_recurring}% sur les suivants
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerDashboard;
